import { Chip, Markdown, Note, Panel, Pill, ProblemBox, Spinner, useLoad } from '@hive/ui';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useT } from '../../i18n';
import { askAi, splitEvidence, splitRefs, type AskContext, type Evidence } from '../../lib/ai';
import { answersStatus, askHive, hasHiveText, safeLink, type HiveAnswer } from '../../lib/answers';
import { useSession } from '../../lib/session';
import { AnswerFeedback } from './AnswerFeedback';

/** Texto da IA com [E1] virando link para a evidência (âncora `ev-E1` na mesma tela). */
export function WithRefs({ text, prefix = 'ev', onRef }: { text: string; prefix?: string; onRef?: (ref: string) => void }) {
  const parts = splitRefs(text);
  return (
    <>
      {parts.map((p, i): ReactNode =>
        'ref' in p ? (
          <a
            key={i}
            href={`#${prefix}-${p.ref}`}
            className="mono"
            onClick={
              onRef
                ? (e) => {
                    e.preventDefault();
                    onRef(p.ref);
                  }
                : undefined
            }
          >
            [{p.ref}]
          </a>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

/** Resposta da IA em Markdown seguro (negrito, itálico, listas, código, links só internos), com [E1] como link. */
export function AnswerText({ text, prefix, onRef }: { text: string; prefix: string; onRef?: (ref: string) => void }) {
  return <Markdown source={text} links="internal" text={(s) => <WithRefs text={s} prefix={prefix} onRef={onRef} />} />;
}

function EvidenceList({ items, prefix, hot }: { items: Evidence[]; prefix: string; hot: string }) {
  return (
    <ol className="small" style={{ margin: 0, paddingLeft: 18 }}>
      {items.map((ev) => (
        <li key={ev.id} id={`${prefix}-${ev.id}`} className={hot === ev.id ? 'ask-ev-hot' : undefined} aria-current={hot === ev.id ? 'true' : undefined}>
          <b className="mono">{ev.id}</b> {ev.label}
        </li>
      ))}
    </ol>
  );
}

/**
 * "Perguntar à IA" (HU-1109): o próprio Hive tenta responder primeiro (R01-14: números exatos, sem modelo, selo
 * "Respondido pelo Hive"); o que ele não souber vai ao modelo, se configurado — resposta em fluxo, com evidências citadas
 * e, se for o caso, um assistente já preenchido. Sem modelo, só o Hive responde e sugere perguntas.
 */
export function AskPanel({
  recommendationId,
  title,
  context,
  initialQuestion,
  label,
  placeholder,
}: {
  recommendationId?: string;
  title?: string;
  context?: AskContext;
  initialQuestion?: string;
  label?: string;
  placeholder?: string;
}) {
  const t = useT();
  const s = useSession();
  const navigate = useNavigate();
  const status = useLoad(() => answersStatus(s.api), [s.api]);
  const [q, setQ] = useState(initialQuestion ?? '');
  const uid = useId().replace(/:/g, '');
  const [answer, setAnswer] = useState('');
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [prefill, setPrefill] = useState<{ wizard?: string; prefill?: Record<string, unknown>; label?: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [streamErr, setStreamErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [hive, setHive] = useState<HiveAnswer | null>(null);
  const [source, setSource] = useState<'hive' | 'model' | null>(null);
  // R01-16: a pergunta respondida e a execução do modelo, para a avaliação do operador
  const [asked, setAsked] = useState('');
  const [runId, setRunId] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const model = !!status.data?.model;
  // recomendação aberta: a pergunta é sobre ela, só o modelo responde
  const enabled = recommendationId ? model : !!status.data?.available || model;
  const { cited, others } = splitEvidence(evidence, answer);
  const [othersOpen, setOthersOpen] = useState(false);
  const [hot, setHot] = useState('');
  // [E1] da resposta: abre "Outras fontes" se for preciso, rola até a evidência e a realça por um instante
  const goTo = (ref: string) => {
    if (others.some((e) => e.id === ref)) setOthersOpen(true);
    setHot(ref);
    window.requestAnimationFrame(() => document.getElementById(`ask-ev-${uid}-${ref}`)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }));
  };
  useEffect(() => {
    if (!hot) return;
    const h = window.setTimeout(() => setHot(''), 2000);
    return () => window.clearTimeout(h);
  }, [hot]);
  // tempo de espera visível: modelos locais pequenos levam dezenas de segundos para a primeira palavra
  const [waited, setWaited] = useState(0);
  useEffect(() => {
    if (!busy) return;
    setWaited(0);
    const h = window.setInterval(() => setWaited((w) => w + 1), 1000);
    return () => window.clearInterval(h);
  }, [busy]);
  const run = async (question: string) => {
    if (!question.trim() || busy) return;
    setBusy(true);
    setAnswer('');
    setEvidence([]);
    setPrefill(null);
    setError(null);
    setStreamErr('');
    setHive(null);
    setSource(null);
    setRunId(null);
    setAsked(question.trim());
    abort.current = new AbortController();
    try {
      if (!model) {
        // sem modelo: só o Hive responde (e sugere perguntas quando não souber)
        const a = await askHive(s.api, question.trim());
        setHive(a);
        setSource('hive');
        return;
      }
      await askAi(
        s.api,
        { question: question.trim(), ...(recommendationId ? { recommendationId } : {}), ...(context ? { context } : {}) },
        {
          onDelta: (x) => setAnswer((a) => a + x),
          onEvidence: setEvidence,
          onPrefill: setPrefill,
          onError: setStreamErr,
          onAnswer: setHive,
          onSource: setSource,
          onRun: setRunId,
        },
        abort.current.signal,
      );
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) setError(err);
    } finally {
      setBusy(false);
    }
  };
  const ask = (e: FormEvent) => {
    e.preventDefault();
    void run(q);
  };
  const pick = (x: string) => {
    setQ(x);
    void run(x);
  };
  const link = safeLink(hive?.link);
  const suggestions = hive && !hive.matched ? (hive.suggestions ?? []) : [];
  return (
    <Panel title={title ?? t('ai.ask.title')}>
      <div className="stack-sm">
        {status.data && !enabled && <Note tone="warn">{t('ai.ask.off', { reason: status.data.modelReason ?? '' })}</Note>}
        {status.data && enabled && !model && !recommendationId && <Note tone="info">{t('ans.onlyHive')}</Note>}
        <form className="stack-sm" onSubmit={ask}>
          <label htmlFor={`ask-${uid}`} className="small">
            {label ?? (recommendationId ? t('ai.ask.aboutRec') : t('ai.ask.label'))}
          </label>
          <textarea
            id={`ask-${uid}`}
            rows={2}
            maxLength={1000}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={!enabled}
            placeholder={placeholder ?? (model ? t('ai.ask.placeholder') : t('ans.placeholder'))}
            title={!enabled ? (status.data?.modelReason ?? undefined) : undefined}
          />
          <div className="row">
            <button type="submit" className="btn btn-sm btn-primary" disabled={!enabled || busy || !q.trim()}>
              {t('ai.ask.send')}
            </button>
            {busy && (
              <>
                <Spinner label={t('ai.ask.thinking')} />
                <span className="xs muted" aria-live="off">
                  {t('ai.ask.waited', { s: waited })}
                </span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => abort.current?.abort()}>
                  {t('ai.ask.stop')}
                </button>
              </>
            )}
          </div>
        </form>
        {busy && !answer && waited >= 10 && <Note tone="info">{t('ai.ask.slow')}</Note>}
        {error ? <ProblemBox error={error} /> : null}
        {streamErr && <Note tone="crit">{streamErr}</Note>}
        {hasHiveText(hive) && hive && (
          <div className="well small stack-sm" aria-live="polite" data-testid="hive-answer">
            <div className="row">
              <Pill tone="ok" title={t('ans.byHiveHint')}>
                {t('ans.byHive')}
              </Pill>
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{hive.text}</div>
            {(hive.filters ?? []).length > 0 && (
              <span className="xs muted">
                {t('ans.filters')}: {(hive.filters ?? []).map((f) => f.label).join(', ')}
              </span>
            )}
            {link && (
              <button type="button" className="btn btn-sm" style={{ width: 'max-content' }} onClick={() => navigate(link)}>
                {t('ans.open')}
              </button>
            )}
          </div>
        )}
        {hive && !hive.matched && source === 'hive' && <Note tone="info">{model ? t('ans.noMatch') : t('ans.noModel')}</Note>}
        {suggestions.length > 0 && source === 'hive' && (
          <div className="stack-sm">
            <span className="xs muted">{t('ans.suggestions')}</span>
            <div className="row" style={{ flexWrap: 'wrap' }}>
              {suggestions.map((x) => (
                <Chip key={x} onClick={() => pick(x)}>
                  {x}
                </Chip>
              ))}
            </div>
          </div>
        )}
        {answer && source !== 'hive' && (
          <div className="row">
            <Pill tone="info" title={t('ans.byModelHint')}>
              {t('ans.byModel')}
            </Pill>
          </div>
        )}
        {answer && (
          <div className="well small" aria-live="polite">
            <AnswerText text={answer} prefix={`ask-ev-${uid}`} onRef={goTo} />
          </div>
        )}
        {evidence.length > 0 && (
          <div className="stack-sm small">
            <span className="xs muted">{t('ai.ask.evidenceHint')}</span>
            {cited.length > 0 && <EvidenceList items={cited} prefix={`ask-ev-${uid}`} hot={hot} />}
            {others.length > 0 && (
              <details open={othersOpen} onToggle={(e) => setOthersOpen(e.currentTarget.open)}>
                <summary>{t('ai.ask.otherSources', { n: others.length })}</summary>
                <EvidenceList items={others} prefix={`ask-ev-${uid}`} hot={hot} />
              </details>
            )}
          </div>
        )}
        {prefill?.wizard === 'alert-rule' && (
          <button
            type="button"
            className="btn btn-sm"
            style={{ width: 'max-content' }}
            onClick={() => navigate('/alerts/rules/new', { state: { prefill: prefill.prefill ?? {}, label: prefill.label || t('rules.prefilledByAi') } })}
          >
            {t('ai.ask.openWizard')}
          </button>
        )}
        {!busy && asked && source === 'hive' && hasHiveText(hive) && hive?.matched && !hive.reason && (
          <AnswerFeedback key={`h-${asked}`} question={asked} answer={hive.text ?? ''} source="hive" intent={hive.intent} />
        )}
        {!busy && asked && source === 'model' && answer && <AnswerFeedback key={`m-${runId ?? asked}`} question={asked} answer={answer} source="model" runId={runId} />}
        <span className="xs muted">{t('ai.ask.privacy')}</span>
      </div>
    </Panel>
  );
}
